import { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { connect } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import logo from "../../assets/nhclogo.png";
import { setCurrentUser } from "../../redux/user/user.actions";
import { selectCurrentUser } from "../../redux/user/user.selectors";
import RoleSelector from "../../components/role-selector/role-selector.component";
import { getApiUrl } from "../../utils/api";

const SignInPage = ({ currentUser, setCurrentUser }) => {
    const otpSubmitting = useRef(false);
    const otpRefs = useRef([]);
    const history = useHistory();

    const [user, setUser]                       = useState({ username: "", password: "", error: false, message: null });
    const [showPassword, setShowPassword]       = useState(false);
    const [loading, setLoading]                 = useState(false);
    const [pendingDualUser, setPendingDualUser] = useState(null);
    const [requires2FA, setRequires2FA]         = useState(false);
    const [pendingUserId, setPendingUserId]     = useState(null);

    const apiUrl = getApiUrl();

    useEffect(() => {
        if (currentUser != null) {
            history.push(currentUser.role === "owner" ? "/user/dashboard" : "/user/profile");
        }
    }, [currentUser]);

    const resetToLogin = (message = null) => {
        setRequires2FA(false);
        setPendingUserId(null);
        otpRefs.current.forEach(r => { if (r) r.value = ""; });
        setUser(prev => ({ ...prev, message, error: !!message }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setUser(prev => ({ ...prev, message: null, error: false }));
        try {
            const response = await fetch(`${apiUrl}/users/authenticate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ username: user.username, password: user.password }),
            });

            if (response.status === 405) {
                setUser(prev => ({ ...prev, message: "Invalid username or password", error: true }));
                return;
            }

            if (!response.ok) {
                const errorData = await response.json();
                setUser(prev => ({ ...prev, message: errorData.message || "An error occurred", error: true }));
                return;
            }

            const data = await response.json();

            if (data.requires2FA) {
                setPendingUserId(data.userId);
                setRequires2FA(true);
                setTimeout(() => otpRefs.current[0]?.focus(), 50);
                return;
            }

            if (!data.message) {
                data.hasDualRole ? setPendingDualUser(data) : setCurrentUser(data);
            } else {
                setUser(prev => ({ ...prev, message: data.message, error: true }));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getOtpValue = () => [0,1,2,3,4,5].map(i => otpRefs.current[i]?.value || "").join("");

    const handleOtpInput = (index, e) => {
        const val = e.target.value.replace(/\D/g, "");
        e.target.value = val.slice(-1);
        if (val && index < 5) otpRefs.current[index + 1]?.focus();
        const full = getOtpValue();
        if (full.length === 6) handleOtpSubmit(full);
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otpRefs.current[index]?.value && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
        if (e.key === "Enter" && getOtpValue().length === 6) {
            handleOtpSubmit(getOtpValue());
        }
    };

    const handleOtpPaste = (e) => {
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        if (!pasted) return;
        e.preventDefault();
        pasted.split("").forEach((ch, i) => { if (otpRefs.current[i]) otpRefs.current[i].value = ch; });
        const full = getOtpValue();
        otpRefs.current[Math.min(pasted.length - 1, 5)]?.focus();
        if (pasted.length === 6) handleOtpSubmit(full);
    };

    const handleOtpSubmit = async (code) => {
        if (otpSubmitting.current) return;
        otpSubmitting.current = true;
        setLoading(true);
        setUser(prev => ({ ...prev, message: null, error: false }));
        try {
            const response = await fetch(`${apiUrl}/users/verify-2fa`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: pendingUserId, code }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errorCode === "OTP_LOCKOUT" || data.errorCode === "SESSION_EXPIRED") {
                    resetToLogin(data.message || "Too many failed attempts. Please sign in again.");
                    return;
                }
                otpRefs.current.forEach(r => { if (r) r.value = ""; });
                setTimeout(() => otpRefs.current[0]?.focus(), 50);
                setUser(prev => ({ ...prev, message: "Invalid code.", error: true }));
                return;
            }

            if (data.hasDualRole) {
                setPendingDualUser(data);
                setRequires2FA(false);
            } else {
                setCurrentUser(data);
            }
        } catch (error) {
            console.error(error);
            setUser(prev => ({ ...prev, message: "An error occurred.", error: true }));
        } finally {
            setLoading(false);
            otpSubmitting.current = false;
        }
    };

    const handleRoleSelect = (role) => {
        const userData = { ...pendingDualUser, role };
        setPendingDualUser(null);
        setCurrentUser(userData);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUser(prev => ({ ...prev, [name]: value }));
    };

    if (pendingDualUser) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark p-4">
                <RoleSelector
                    onSelectRole={handleRoleSelect}
                    userName={pendingDualUser.firstName}
                    distros={pendingDualUser.distros}
                    onSignOut={() => setPendingDualUser(null)}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark p-4">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-modal p-8 md:p-10">

                <div className="flex justify-center mb-6">
                    <img src={logo} alt="National HealthClaim" className="w-56" />
                </div>

                <div className="text-center text-xs font-medium tracking-widest text-gray-600 mb-6">
                    ADVISOR PORTAL
                </div>

                {user.message && (
                    <div className={`mb-4 p-3 rounded-xl text-sm flex items-center gap-2 ${
                        user.error
                            ? "bg-red-50 border border-red-200 text-red-600"
                            : "bg-green-50 border border-green-200 text-green-600"
                    }`}>
                        {user.error
                            ? <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            : <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        }
                        <span>{user.message}</span>
                    </div>
                )}

                {requires2FA ? (
                    <>
                        <p className="text-center text-sm text-gray-500 mb-6">
                            A 6-digit verification code has been sent to your email.
                        </p>

                        <div className="flex gap-2 justify-center mb-6">
                            {[0,1,2,3,4,5].map((i) => (
                                <input
                                    key={i}
                                    ref={el => otpRefs.current[i] = el}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    autoComplete={i === 0 ? "one-time-code" : "off"}
                                    onChange={e => handleOtpInput(i, e)}
                                    onKeyDown={e => handleOtpKeyDown(i, e)}
                                    onPaste={i === 0 ? handleOtpPaste : undefined}
                                    disabled={loading}
                                    className="w-11 h-14 text-center text-xl font-semibold border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200"
                                />
                            ))}
                        </div>

                        <button
                            onClick={() => handleOtpSubmit(getOtpValue())}
                            disabled={loading || getOtpValue().length < 6}
                            className="w-full bg-primary text-white py-4 px-6 rounded-full font-semibold text-base shadow-sm hover:bg-primary-dark active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                            {loading ? "Verifying..." : "Verify"}
                        </button>

                        <button
                            onClick={() => resetToLogin()}
                            className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            ← Back to sign in
                        </button>
                    </>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email address
                            </label>
                            <input
                                type="text"
                                name="username"
                                value={user.username}
                                onChange={handleChange}
                                placeholder="Enter your email"
                                required
                                className="w-full p-4 border border-gray-200 rounded-xl text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200"
                            />
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={user.password}
                                    onChange={handleChange}
                                    placeholder="Enter your password"
                                    required
                                    className="w-full p-4 pr-12 border border-gray-200 rounded-xl text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200"
                                />
                                <button
                                    type="button"
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    onMouseDown={() => setShowPassword(true)}
                                    onMouseUp={() => setShowPassword(false)}
                                    onMouseLeave={() => setShowPassword(false)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-primary text-white py-4 px-6 rounded-full font-semibold text-base shadow-sm hover:bg-primary-dark active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                            {loading ? "Signing In..." : "Sign In"}
                        </button>

                        <Link
                            to="/resetpassword"
                            className="block mt-4 text-center text-sm text-primary hover:text-primary-dark transition-colors"
                        >
                            Forgot password or first time signing in?
                        </Link>

                        <div className="mt-6 text-center text-xs text-gray-500">
                            If you're having issues signing in, please{" "}
                            <a href="mailto:support@nationalhealthclaim.com" className="text-xs text-primary hover:text-primary-dark transition-colors">
                                contact support
                            </a>.
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
    setCurrentUser: (data) => dispatch(setCurrentUser(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignInPage);
